/** Responsibilities panel — assign CP to CPR sections. */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  listCPProfiles, listCPResponsibilities, addCPResponsibility, removeCPResponsibility,
} from "@/lib/api";
import { CPField, CPTextInput } from "@/pages/wizard/cp/_cpShared";


export default function CPResponsibilitiesPanel({ projectId, summary, onRefresh }) {
  const qc = useQueryClient();
  const profilesQ = useQuery({
    queryKey: ["cp-profiles", projectId],
    queryFn:  () => listCPProfiles(projectId),
    enabled:  !!projectId,
  });
  const respQ = useQuery({
    queryKey: ["cp-responsibilities", projectId],
    queryFn:  () => listCPResponsibilities(projectId),
    enabled:  !!projectId,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cp_profile_id: "", section: "", responsibility_type: "Primary",
    limitations: "", effective_date: "",
  });

  const add = useMutation({
    mutationFn: () => addCPResponsibility(projectId, form),
    onSuccess:  () => {
      toast.success("Responsibility assigned");
      qc.invalidateQueries({ queryKey: ["cp-responsibilities", projectId] });
      onRefresh?.();
      setOpen(false);
      setForm({ ...form, section: "" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message),
  });

  const del = useMutation({
    mutationFn: (rid) => removeCPResponsibility(projectId, rid),
    onSuccess:  () => {
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["cp-responsibilities", projectId] });
      onRefresh?.();
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = respQ.data?.responsibilities || [];
  const unassigned = respQ.data?.unassigned || [];
  const sections = summary?.cpr_sections || [];
  const profiles = profilesQ.data?.profiles || [];
  const profileById = (id) => profiles.find((p) => p.id === id);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {rows.length} assignment(s) · {unassigned.length} section(s) unassigned
        </div>
        <Button size="sm" onClick={() => setOpen(true)}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    disabled={profiles.length === 0}
                    data-testid="w-cp-resp-new">
          <Plus size={11} className="mr-1" /> Assign responsibility
        </Button>
      </div>

      {profiles.length === 0 && (
        <div className="text-[11px] text-amber-600 flex items-center gap-1">
          <Warning size={11} /> Add a CP profile first before assigning responsibilities.
        </div>
      )}

      {unassigned.length > 0 && (
        <div className="border-l-2 border-amber-400 bg-amber-50 p-2 text-[11px]">
          <Warning size={11} className="inline mr-1 -mt-0.5 text-amber-500" />
          <b>Unassigned CPR sections:</b>{" "}
          <span className="font-mono">{unassigned.join(", ")}</span>
        </div>
      )}

      <div className="rounded border border-slate-200 overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-2 py-1">Section</th>
              <th className="text-left px-2 py-1">CP</th>
              <th className="text-left px-2 py-1">Type</th>
              <th className="text-left px-2 py-1">Limitations</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="5" className="text-center py-4 text-slate-500">No assignments yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-200"
                    data-testid={`w-cp-resp-${r.id}`}>
                <td className="px-2 py-1 font-bold text-slate-800">{r.section}</td>
                <td className="px-2 py-1">{profileById(r.cp_profile_id)?.full_name || "—"}</td>
                <td className="px-2 py-1">
                  <span className={`px-1.5 py-0.5 rounded ${
                    r.responsibility_type === "Primary"
                      ? "bg-brand-blue/10 text-brand-blue"
                      : "bg-slate-100 text-slate-600"
                  }`}>{r.responsibility_type}</span>
                </td>
                <td className="px-2 py-1 text-slate-500">{r.limitations || "—"}</td>
                <td className="px-2 py-1">
                  <button type="button" onClick={() => del.mutate(r.id)}
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`w-cp-resp-del-${r.id}`}>
                    <Trash size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign CPR responsibility</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <CPField label="CP profile">
              <Select value={form.cp_profile_id}
                        onValueChange={(v) => setForm({ ...form, cp_profile_id: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-resp-profile">
                  <SelectValue placeholder="Select CP" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Section">
              <Select value={form.section}
                        onValueChange={(v) => setForm({ ...form, section: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-resp-section">
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent>
                  {sections.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Responsibility type">
              <Select value={form.responsibility_type}
                        onValueChange={(v) => setForm({ ...form, responsibility_type: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-resp-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Primary">Primary</SelectItem>
                  <SelectItem value="Supporting">Supporting</SelectItem>
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Limitations / reliance (optional)">
              <CPTextInput value={form.limitations}
                             onChange={(v) => setForm({ ...form, limitations: v })}
                             placeholder="e.g. Metallurgy reliant on Snowden Report Feb 2025"
                             testId="w-cp-resp-lim" />
            </CPField>
          </div>
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white"
                       disabled={!form.cp_profile_id || !form.section || add.isPending}
                       onClick={() => add.mutate()}
                       data-testid="w-cp-resp-save">
              {add.isPending ? "Saving…" : "Assign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
